
import { useParams, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Helmet } from "react-helmet-async";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { fetchOfferById, fetchOfferBySlug } from "@/lib/api/offers/queries";
import { useSellerData } from "@/hooks/useSellerData";
import { useState, useEffect } from "react";
import OpenGraphImage from "@/components/common/OpenGraphImage";
import { generateOgDescription, getOgImagePath } from "@/lib/utils/ogImageUtils";

// Import components
import OfferHeader from "@/components/offer/OfferHeader";
import OfferMetrics from "@/components/offer/OfferMetrics";
import SellerInfo from "@/components/offer/SellerInfo";
import OfferActions from "@/components/offer/OfferActions";
import ReviewSection from "@/components/offer/ReviewSection";

const OfferDetails = () => {
  const { toast } = useToast();
  const { offerId } = useParams<{ offerId: string }>();
  const [isSlug, setIsSlug] = useState<boolean | null>(null);
  
  useEffect(() => {
    // Check if the offerId parameter looks like a UUID or slug
    if (offerId) {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      setIsSlug(!uuidRegex.test(offerId));
    }
  }, [offerId]);
  
  const { data: offer, isLoading, isError } = useQuery({
    queryKey: ['offer', offerId, isSlug],
    queryFn: async () => {
      if (!offerId) return null;
      
      console.log(`Fetching offer with ${isSlug ? 'slug' : 'ID'}: ${offerId}`);
      
      if (isSlug) {
        const result = await fetchOfferBySlug(offerId);
        console.log(`Slug fetch result for ${offerId}:`, result);
        return result;
      } else {
        const result = await fetchOfferById(offerId);
        console.log(`ID fetch result for ${offerId}:`, result);
        return result;
      }
    },
    enabled: offerId !== undefined && isSlug !== null,
    staleTime: 0, // Consider all data stale immediately
    gcTime: 1000 * 60, // Cache for 1 minute (renamed from cacheTime to gcTime)
    refetchOnMount: true, // Always refetch on mount
    meta: {
      onSettled: (data, error) => {
        console.log("Query settled for offer:", offerId);
        console.log("Received offer data:", data);
        
        if (error) {
          console.error("Error in offer query:", error);
          toast({
            title: "Error",
            description: "Failed to load offer details",
            variant: "destructive",
          });
        }
      }
    },
  });

  // Make sure we have a valid seller ID before trying to fetch seller data
  const sellerProfileId = offer?.seller_profile_id || "";
  const sellerSlug = offer?.seller_profile?.slug || "";
  
  console.log("Current offer data:", {
    id: offer?.id,
    title: offer?.title,
    slug: offer?.slug,
    sellerProfileId,
    sellerSlug
  });
  
  const {
    seller,
    reviews,
    isLoading: isLoadingSeller,
  } = useSellerData(sellerProfileId || sellerSlug || "");

  const handlePurchase = () => {
    toast({
      title: "Purchase Initiated",
      description: "Redirecting to checkout...",
    });
  };

  const getSellerInfo = () => {
    if (!offer) return {
      name: "Unknown Seller",
      verified: false,
      logo: "/placeholder.svg",
      rating: "N/A",
      reviewCount: 0,
      bio: "No bio provided.",
      sellerId: "",
      offerCount: 0,
      slug: undefined,
    };

    const sellerName = offer.seller_profile?.name || seller?.name || "Unknown Seller";
    return {
      name: sellerName,
      verified: typeof offer.seller_profile?.verified === 'boolean' 
        ? offer.seller_profile.verified 
        : seller?.verified || false,
      logo: seller?.logo_url || "/placeholder.svg",
      rating: reviews && reviews.length > 0
        ? (reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length).toFixed(1)
        : "N/A",
      reviewCount: reviews?.length || 0,
      bio: seller?.bio || "No bio provided.",
      sellerId: offer.seller_profile_id || "",
      offerCount: 0, // This would need to be retrieved from the seller data
      slug: sellerSlug,
    };
  };

  // Redirect to slug URL if we loaded the offer by ID and it has a slug
  if (!isLoading && offer && !isSlug && offer.slug && offerId !== offer.slug) {
    console.log(`Redirecting to slug URL: /offers/${offer.slug}`);
    return <Navigate to={`/offers/${offer.slug}`} replace />;
  }

  if (!isLoading && !offer) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Offer Not Found</h2>
        <p className="text-gray-600 mb-6">
          The offer you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/marketplace">
          <Button>Browse Marketplace</Button>
        </Link>
      </div>
    );
  }

  return (
    <>
      {offer && (
        <OpenGraphImage 
          title={`${offer.title} | BackLink Hub`}
          description={generateOgDescription('offer', offer)}
          image={getOgImagePath('offer', offer)}
          type="product"
        />
      )}
      
      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="space-y-6">
              <div className="h-8 bg-gray-200 rounded w-3/4 animate-pulse"></div>
              <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
                <div className="md:col-span-2 h-64 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          ) : isError ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Failed to Load Offer</h2>
              <p className="text-gray-600 mb-6">
                There was a problem loading the offer details. Please try again.
              </p>
              <Button onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </div>
          ) : offer && (
            <>
              <OfferHeader 
                title={offer.title}
                tags={offer.tags}
                sellerName={getSellerInfo().name}
                sellerLogo={getSellerInfo().logo}
                sellerVerified={getSellerInfo().verified}
                sellerRating={getSellerInfo().rating}
                reviewCount={getSellerInfo().reviewCount}
              />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                  <OfferMetrics 
                    dr={offer.dr}
                    da={offer.da}
                    trafficEstimate={offer.traffic_estimate}
                    sampleLinks={offer.sample_links}
                    requirements={offer.requirements}
                  />
                  
                  <ReviewSection 
                    reviews={reviews || []}
                    isLoading={isLoadingSeller}
                    sellerId={sellerProfileId}
                    reviewCount={reviews?.length || 0}
                  />
                </div>
                
                <div className="space-y-6">
                  <OfferActions 
                    price={offer.price_cents}
                    linkType={offer.link_type}
                    turnaroundDays={offer.turnaround_days}
                    onPurchase={handlePurchase}
                  />
                  
                  <SellerInfo {...getSellerInfo()} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default OfferDetails;
